import {
  AddIconButton,
  Button,
  CloseIconButton
} from '@library/components/buttons';
import {
  Combobox,
  type ComboboxOption
} from '@library/components/selects/combobox/Combobox';
import { useCallback, useEffect, useState } from 'react';
import { DEFAULT_ROLE_OPTION } from '@bo-constants/roles';
import type { RoleWithPermissions } from '@library/types/role';
import type { TransactionMaxValues } from '@library/types/permissions';
import type { UserRoleOption } from '@library/types/user';
import { UserRoleTransactionMax } from '@bo-components/userRoles/UserRoleTransactionMax';
import chevronDownIcon from '@library/assets/images/icons/chevron-down-icon.svg';
import { classNames } from '@library/utils';
import { getTransactionMaxOptionsByRoleId } from '@bo-utils/user';
import { isValidArrayWithValues } from '@bo-utils/common';
import { v4 as uuid } from 'uuid';

export interface UserRoleSettingsProps {
  className?: string;
  leagueOptions: Array<ComboboxOption>;
  onEditUserRoles: (userRoles: Array<UserRoleOption>) => void;
  onViewPermissions?: () => void;
  roleOptions: Array<ComboboxOption>;
  rolesWithPermissions?: Array<RoleWithPermissions>;
  userRoleOptions: Array<UserRoleOption>;
}

export const UserRoleSettings = ({
  className = '',
  leagueOptions,
  onEditUserRoles,
  onViewPermissions = undefined,
  roleOptions,
  rolesWithPermissions = undefined,
  userRoleOptions
}: UserRoleSettingsProps) => {
  const [editUserRoles, setEditUserRoles] =
    useState<Array<UserRoleOption>>(userRoleOptions);

  useEffect(() => {
    onEditUserRoles(editUserRoles);
  }, [editUserRoles, onEditUserRoles]);

  const handleRoleChange = useCallback(
    (newValue: ComboboxOption | null, id: string) => {
      const transactionMaxes = getTransactionMaxOptionsByRoleId(
        rolesWithPermissions,
        String(newValue?.value)
      );

      setEditUserRoles((prevUserRole) =>
        prevUserRole.map((userRole) =>
          userRole.id === id
            ? {
                ...userRole,
                role: newValue,
                transactionMaxes
              }
            : userRole
        )
      );
    },
    [rolesWithPermissions]
  );

  const handleLeaguesChange = useCallback(
    (newValues: Array<ComboboxOption> | null, id: string) => {
      setEditUserRoles((prevUserRole) =>
        prevUserRole.map((userRole) =>
          userRole.id === id ? { ...userRole, leagues: newValues } : userRole
        )
      );
    },
    []
  );

  const handleTransactionMaxChange = useCallback(
    (newValues: Array<TransactionMaxValues> | null, id: string) => {
      setEditUserRoles((prevUserRoles) =>
        prevUserRoles.map((userRole) =>
          userRole.id === id
            ? { ...userRole, transactionMaxes: newValues }
            : userRole
        )
      );
    },
    []
  );

  const handleCloseRole = useCallback((id: string) => {
    setEditUserRoles(
      (prevUserRole) =>
        prevUserRole?.filter((userRole) => userRole.id !== id) ?? []
    );
  }, []);

  const handleAddRoleOption = useCallback(() => {
    setEditUserRoles((prevRoles) => [...prevRoles, DEFAULT_ROLE_OPTION]);
  }, []);

  const handleToggleSettings = useCallback((id: string) => {
    setEditUserRoles((prevUserRole) =>
      prevUserRole.map((userRole) =>
        userRole.id === id
          ? { ...userRole, isSettingOpen: !userRole.isSettingOpen }
          : userRole
      )
    );
  }, []);

  const addRoleDisabled =
    !isValidArrayWithValues(editUserRoles, 'role') &&
    editUserRoles?.length !== 0;

  return (
    <div
      className={classNames('flex flex-col gap-y-3 p-4', className)}
      data-testid="EditUserDetails"
    >
      {editUserRoles?.map((userRole, index) => (
        <div
          key={uuid()}
          className="bg-soFresh-120 border-soFresh-140 relative flex flex-col gap-y-1 rounded-md border border-solid p-2"
        >
          <CloseIconButton
            className="absolute right-2"
            handleOnClick={() => handleCloseRole(userRole.id)}
          />
          <Combobox
            id="role"
            label={`Role ${index + 1}`}
            labelClassName="text-soClean-140 rounded-md text-xs uppercase leading-6 pl-0.5"
            isMulti={false}
            value={userRole.role}
            placeholder="Select role..."
            onChange={(newValue) => handleRoleChange(newValue, userRole.id)}
            options={roleOptions}
          />
          {!!userRole.role && (
            <div className="pl-1 pr-1.5 pt-1">
              <Button
                className="flex w-full items-center justify-between"
                type="button"
                onClick={() => handleToggleSettings(userRole.id)}
              >
                <span className="text-soClean-100 text-xs">Settings</span>
                <img
                  alt="Chevron Down icon"
                  className={classNames(
                    userRole.isSettingOpen ? 'rotate-180' : ''
                  )}
                  src={chevronDownIcon}
                />
              </Button>
              {userRole.isSettingOpen && (
                <>
                  <Combobox
                    id="leaguePerms"
                    isMulti
                    label="League-Specific Permissions"
                    labelClassName="text-soClean-140 rounded-md text-xs uppercase leading-6 py-1 flex"
                    value={userRole.leagues}
                    placeholder="Select league-specific permissions..."
                    onChange={(value) =>
                      handleLeaguesChange(value, userRole.id)
                    }
                    options={leagueOptions}
                    allowSelectAll
                  />

                  <div className="py-1">
                    {!!userRole.transactionMaxes?.length && (
                      <UserRoleTransactionMax
                        transactionMaxes={userRole.transactionMaxes}
                        onTransactionMaxChange={(value) =>
                          handleTransactionMaxChange(value, userRole.id)
                        }
                      />
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      ))}
      <div className="mb-1.5 mt-3 flex justify-between">
        <AddIconButton
          className="bg-soFresh-120 hover:bg-soFresh-120 text-soClean-100 h-10 w-auto border-none px-3 py-2"
          disabled={addRoleDisabled}
          label="Add New Role"
          onClick={handleAddRoleOption}
        />
        {!!onViewPermissions && (
          <Button
            onClick={onViewPermissions}
            className="text-msJackson cursor-pointer text-xs"
          >
            View All Permissions
          </Button>
        )}
      </div>
    </div>
  );
};
